import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import RichTextEditor from '../../components/forms/RichTextEditor';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import PushPinIcon from '@mui/icons-material/PushPin';
import PublishIcon from '@mui/icons-material/Publish';
import ArchiveIcon from '@mui/icons-material/Archive';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import {
  NewsClient,
  NewsItemDto,
  NewsCommentDto,
  NewsEngagementSummaryDto,
  CreateUpdateNewsItemDto,
  getNewsItemStatus,
} from '../../app/services/api/news';

const newsClient = new NewsClient();

const NewsItemDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo, stores, selectedFranchise } = useContext(UserContext);

  const isCreate = !id || id === 'new';
  const canCreate = hasPermissionTo ? hasPermissionTo(['NewsManagementCreate', 'ConfigurationAdmin']) : true;
  const canEdit = hasPermissionTo ? hasPermissionTo(['NewsManagementEdit', 'ConfigurationAdmin']) : true;

  const [newsItem, setNewsItem] = useState<NewsItemDto | null>(null);
  const [comments, setComments] = useState<NewsCommentDto[]>([]);
  const [engagement, setEngagement] = useState<NewsEngagementSummaryDto | null>(null);

  const [formValues, setFormValues] = useState<{
    title: string;
    summary: string;
    content: string;
    imageUrl: string;
    isGlobal: boolean;
    storeNumbers: string[];
    isPinned: boolean;
    publishedAtUtc: string;
    visibleFromUtc: string;
    expiresAtUtc: string;
    tags: number[];
  }>({
    title: '',
    summary: '',
    content: '',
    imageUrl: '',
    isGlobal: true,
    storeNumbers: [],
    isPinned: false,
    publishedAtUtc: '',
    visibleFromUtc: '',
    expiresAtUtc: '',
    tags: [],
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [availableTags, setAvailableTags] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    // Fetch available tags on mount
    newsClient.getTags({ activeOnly: true })
      .then(tags => setAvailableTags(tags.map(t => ({ id: t.id, name: t.name }))))
      .catch(() => setAvailableTags([]));
  }, []);

  useEffect(() => {
    if (!isCreate && id) {
      loadNewsItem(Number(id));
    }
  }, [id, isCreate]);

  const loadNewsItem = async (newsItemId: number) => {
    try {
      const [itemData, commentsData, engagementData] = await Promise.all([
        newsClient.getNewsItem(newsItemId),
        newsClient.getComments(newsItemId),
        newsClient.getEngagementSummary(newsItemId),
      ]);

      if (itemData) {
        setNewsItem(itemData);
        const visibleFrom = itemData.visibleFromUtc ?? itemData.publishedAtUtc;
        setFormValues({
          title: itemData.title,
          summary: itemData.summary,
          content: itemData.content,
          imageUrl: itemData.imageUrl ?? '',
          isGlobal: itemData.isGlobal,
          storeNumbers: itemData.storeNumbers ?? [],
          isPinned: itemData.isPinned,
          publishedAtUtc: itemData.publishedAtUtc ?? '',
          visibleFromUtc: visibleFrom ? visibleFrom.split('T')[0] : '',
          expiresAtUtc: itemData.expiresAtUtc ? itemData.expiresAtUtc.split('T')[0] : '',
          tags: itemData.tags ?? [],
        });
      }
      setComments(commentsData);
      setEngagement(engagementData);
    } catch {
      // Handle error
    }
  };

  if (isCreate && !canCreate) {
    return (
      <PageArea>
        <PageTitle>News Item</PageTitle>
        <Pill text='You do not have permission to create news items.' />
      </PageArea>
    );
  }

  if (!isCreate && !canEdit) {
    return (
      <PageArea>
        <PageTitle>News Item</PageTitle>
        <Pill text='You do not have permission to edit news items.' />
      </PageArea>
    );
  }

  const validate = () => {
    const e: { [key: string]: string } = {};
    if (!formValues.title.trim()) e.title = 'Title is required';
    if (!formValues.summary.trim()) e.summary = 'Summary is required';
    if (!formValues.content.trim()) e.content = 'Content is required';
    if (!formValues.isGlobal && formValues.storeNumbers.length === 0) {
      e.storeNumbers = 'Select at least one store or enable global visibility';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async (publishNow: boolean = false) => {
    if (!validate()) return;
    setIsSaving(true);

    try {
      const dto: CreateUpdateNewsItemDto = {
        title: formValues.title.trim(),
        summary: formValues.summary.trim(),
        content: formValues.content.trim(),
        imageUrl: formValues.imageUrl.trim() || null,
        franchiseId: selectedFranchise?.franchiseId ?? 0,
        isGlobal: formValues.isGlobal,
        storeNumbers: formValues.isGlobal ? null : formValues.storeNumbers,
        isPinned: formValues.isPinned,
        publishedAtUtc: publishNow ? new Date().toISOString() : (newsItem?.publishedAtUtc ?? null),
        visibleFromUtc: formValues.visibleFromUtc ? new Date(formValues.visibleFromUtc).toISOString() : null,
        expiresAtUtc: formValues.expiresAtUtc ? new Date(formValues.expiresAtUtc).toISOString() : null,
        tags: formValues.tags.length > 0 ? formValues.tags : null,
      };

      if (isCreate) {
        await newsClient.createNewsItem(dto);
      } else if (newsItem) {
        await newsClient.updateNewsItem(newsItem.id, dto);
      }

      navigate('/news-management');
    } catch {
      // Handle error
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/news-management');
  };

  const handlePublish = async () => {
    await handleSave(true);
  };

  const handleArchive = async () => {
    if (!newsItem) return;
    try {
      await newsClient.updateNewsItem(newsItem.id, {
        title: newsItem.title,
        content: newsItem.content,
        summary: newsItem.summary,
        imageUrl: newsItem.imageUrl,
        franchiseId: newsItem.franchiseId,
        storeNumbers: newsItem.storeNumbers,
        isGlobal: newsItem.isGlobal,
        isPinned: newsItem.isPinned,
        publishedAtUtc: newsItem.publishedAtUtc,
        visibleFromUtc: newsItem.visibleFromUtc,
        expiresAtUtc: new Date().toISOString(), // Set to now to expire/archive
        tags: newsItem.tags,
      });
      navigate('/news-management');
    } catch {
      // Handle error
    }
  };

  const handleTogglePin = async () => {
    if (!newsItem) return;
    try {
      const updated = await newsClient.updateNewsItem(newsItem.id, {
        title: newsItem.title,
        content: newsItem.content,
        summary: newsItem.summary,
        imageUrl: newsItem.imageUrl,
        franchiseId: newsItem.franchiseId,
        storeNumbers: newsItem.storeNumbers,
        isGlobal: newsItem.isGlobal,
        isPinned: !newsItem.isPinned,
        publishedAtUtc: newsItem.publishedAtUtc,
        visibleFromUtc: newsItem.visibleFromUtc,
        expiresAtUtc: newsItem.expiresAtUtc,
        tags: newsItem.tags,
      });
      setNewsItem(updated);
      setFormValues(prev => ({ ...prev, isPinned: updated.isPinned }));
    } catch {
      // Handle error
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await newsClient.deleteComment(commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      if (engagement) {
        setEngagement({ ...engagement, commentCount: engagement.commentCount - 1 });
      }
    } catch {
      // Handle error
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const title = isCreate ? 'Create News Item' : 'Edit News Item';

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <PageTitle subTitle={isCreate ? 'Create a new news item for your stores' : 'Edit news item details and view engagement'}>
              {title}
            </PageTitle>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="secondary"
                onClick={handleBack}
                disabled={isSaving}>
                Cancel
              </Button>
              {!isCreate && newsItem && (
                <>
                  <Button
                    variant="secondary"
                    startIcon={<PushPinIcon />}
                    onClick={handleTogglePin}
                    disabled={isSaving}
                  >
                    {formValues.isPinned ? 'Unpin' : 'Pin'}
                  </Button>
                  {getNewsItemStatus(newsItem.publishedAtUtc, newsItem.expiresAtUtc) === 'Published' && (
                    <Button
                      variant="secondary"
                      startIcon={<ArchiveIcon />}
                      onClick={handleArchive}
                      disabled={isSaving}
                    >
                      Archive
                    </Button>
                  )}
                </>
              )}
              <Button variant="primary"
                onClick={() => handleSave(false)}
                disabled={isSaving}>
                {isSaving ? 'Saving...' : isCreate ? 'Create News Item' : 'Save Changes'}
              </Button>
              {isCreate && (
                <Button
                  variant="primary"
                  startIcon={<PublishIcon />}
                  onClick={handlePublish}
                  disabled={isSaving}
                >
                  Create & Publish
                </Button>
              )}
              {!isCreate && newsItem && getNewsItemStatus(newsItem.publishedAtUtc, newsItem.expiresAtUtc) === 'Draft' && (
                <Button
                  variant="primary"
                  startIcon={<PublishIcon />}
                  onClick={handlePublish}
                  disabled={isSaving}
                >
                  Publish Now
                </Button>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Engagement Stats (for existing items) */}
        {!isCreate && engagement && (
          <Grid item
            xs={12}>
            <Section style={{ p: 4 }} >
              <Box sx={{ p: 3 }}>
                <Grid container
                  spacing={3}>
                  <Grid item
                    xs={6}
                    md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <VisibilityIcon color="action" />
                        <Typography variant="displaySM">{engagement.viewCount}</Typography>
                      </Box>
                      <Typography variant="textSM"
                        color="text.secondary">Total Views</Typography>
                    </Box>
                  </Grid>
                  <Grid item
                    xs={6}
                    md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <ThumbUpIcon color="action" />
                        <Typography variant="displaySM">{engagement.likeCount}</Typography>
                      </Box>
                      <Typography variant="textSM"
                        color="text.secondary">Likes</Typography>
                    </Box>
                  </Grid>
                  <Grid item
                    xs={6}
                    md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <CommentIcon color="action" />
                        <Typography variant="displaySM">{engagement.commentCount}</Typography>
                      </Box>
                      <Typography variant="textSM"
                        color="text.secondary">Comments</Typography>
                    </Box>
                  </Grid>
                  <Grid item
                    xs={6}
                    md={3}>
                    <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="displaySM">{engagement.engagementRate.toFixed(1)}%</Typography>
                      <Typography variant="textSM"
                        color="text.secondary">Engagement Rate</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Section>
          </Grid>
        )}

        {/* Content Section */}
        <Grid item
          xs={12}
          md={8}>
          <Section>
            <TableHeader>Content</TableHeader>
            <Box sx={{ p: 3 }}>
              <Grid container
                spacing={3}>
                <Grid item
                  xs={12}>
                  <TextField
                    label="Title"
                    fullWidth
                    required
                    value={formValues.title}
                    onChange={e => setFormValues(prev => ({ ...prev, title: e.target.value }))}
                    error={!!errors.title}
                    helperText={errors.title}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    label="Summary"
                    fullWidth
                    required
                    multiline
                    rows={2}
                    value={formValues.summary}
                    onChange={e => setFormValues(prev => ({ ...prev, summary: e.target.value }))}
                    error={!!errors.summary}
                    helperText={errors.summary || 'Brief description shown in the news feed'}
                  />
                </Grid>
                <Grid item
                  xs={12}>
                  <Box>
                    <Typography
                      variant="textSM"
                      sx={{ mb: 1, fontWeight: 500 }}
                    >
                      Content
                    </Typography>
                    <RichTextEditor
                      value={formValues.content}
                      onChange={(value) => setFormValues(prev => ({ ...prev, content: value }))}
                      placeholder="Enter the full article content..."
                      enableDocumentLinking={false}
                    />
                  </Box>
                </Grid>
                <Grid item
                  xs={12}>
                  <TextField
                    label="Image URL (optional)"
                    fullWidth
                    value={formValues.imageUrl}
                    onChange={e => setFormValues(prev => ({ ...prev, imageUrl: e.target.value }))}
                    helperText="URL to a header image for this news item"
                  />
                </Grid>
                {formValues.imageUrl && (
                  <Grid item
                    xs={12}>
                    <Box sx={{ maxWidth: 400 }}>
                      <img
                        src={formValues.imageUrl}
                        alt="Preview"
                        style={{ width: '100%', borderRadius: 8 }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Section>
        </Grid>

        {/* Settings Section */}
        <Grid item
          xs={12}
          md={4}>
          <Section>
            <TableHeader>Settings</TableHeader>
            <Box sx={{ p: 3 }}>
              <Grid container
                spacing={3}>
                {!isCreate && newsItem && (
                  <Grid item
                    xs={12}>
                    <TextField
                      label="Status"
                      fullWidth
                      value={getNewsItemStatus(newsItem.publishedAtUtc, newsItem.expiresAtUtc)}
                      disabled
                      helperText="Status is determined by publish and expiry dates"
                    />
                  </Grid>
                )}

                <Grid item
                  xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formValues.isPinned}
                        onChange={e => setFormValues(prev => ({ ...prev, isPinned: e.target.checked }))}
                      />
                    }
                    label="Pin to top of news feed"
                  />
                </Grid>

                <Grid item
                  xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="textMD"
                    sx={{ mb: 1 }}>Visibility</Typography>
                </Grid>

                <Grid item
                  xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formValues.isGlobal}
                        onChange={e => setFormValues(prev => ({
                          ...prev,
                          isGlobal: e.target.checked,
                          storeNumbers: e.target.checked ? [] : prev.storeNumbers,
                        }))}
                      />
                    }
                    label="Visible to all stores"
                  />
                </Grid>

                {!formValues.isGlobal && (
                  <Grid item
                    xs={12}>
                    <Autocomplete
                      multiple
                      options={stores?.map(s => s.storeNumber) ?? ['001', '002', '003', '004', '005']}
                      getOptionLabel={(storeNumber) => {
                        const store = stores?.find(s => s.storeNumber === storeNumber);
                        return store?.storeName ? `${storeNumber} - ${store.storeName}` : (storeNumber ?? '');
                      }}
                      value={formValues.storeNumbers}
                      onChange={(_, value) => setFormValues(prev => ({ ...prev, storeNumbers: value as string[] }))}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Stores"
                          error={!!errors.storeNumbers}
                          helperText={errors.storeNumbers}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              variant="outlined"
                              label={`Store ${option}`}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                    />
                  </Grid>
                )}

                <Grid item
                  xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="textMD"
                    sx={{ mb: 1 }}>Scheduling</Typography>
                </Grid>

                <Grid item
                  xs={12}>
                  <TextField
                    label="Visible From"
                    type="date"
                    fullWidth
                    value={formValues.visibleFromUtc}
                    onChange={e => setFormValues(prev => ({ ...prev, visibleFromUtc: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty to show immediately when published"
                  />
                </Grid>

                <Grid item
                  xs={12}>
                  <TextField
                    label="Expires On"
                    type="date"
                    fullWidth
                    value={formValues.expiresAtUtc}
                    onChange={e => setFormValues(prev => ({ ...prev, expiresAtUtc: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    helperText="Leave empty for no expiration"
                  />
                </Grid>

                <Grid item
                  xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="textMD"
                    sx={{ mb: 1 }}>Tags</Typography>
                </Grid>

                <Grid item
                  xs={12}>
                  <Autocomplete
                    multiple
                    options={availableTags}
                    value={availableTags.filter(t => formValues.tags.includes(t.id))}
                    onChange={(_, value) => setFormValues(prev => ({ ...prev, tags: value.map(v => v.id) }))}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        placeholder="Select tags"
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            variant="outlined"
                            label={option.name}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>
          </Section>

          {/* Meta Info (for existing items) */}
          {!isCreate && newsItem && (
            <Section style={{ mt: 3 }}>
              <TableHeader>Information</TableHeader>
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                <Typography variant="textSM"
                  color="text.secondary"
                  gutterBottom>
                  Created by: {newsItem.createdByUserName}
                </Typography>
                <Typography variant="textSM"
                  color="text.secondary"
                  gutterBottom>
                  Created: {formatDate(newsItem.createdAtUtc)}
                </Typography>
                {newsItem.updatedByUserName && (
                  <>
                    <Typography variant="textSM"
                      color="text.secondary"
                      gutterBottom>
                      Updated by: {newsItem.updatedByUserName}
                    </Typography>
                    <Typography variant="textSM"
                      color="text.secondary"
                      gutterBottom>
                      Updated: {formatDate(newsItem.updatedAtUtc)}
                    </Typography>
                  </>
                )}
                {newsItem.publishedAtUtc && (
                  <Typography variant="textSM"
                    color="text.secondary"
                    gutterBottom>
                    Published: {formatDate(newsItem.publishedAtUtc)}
                  </Typography>
                )}
              </Box>
            </Section>
          )}
        </Grid>

        {/* Comments Section (for existing items) */}
        {!isCreate && comments.length > 0 && (
          <Grid item
            xs={12}>
            <Section>
              <TableHeader>Comments ({comments.length})</TableHeader>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Author</TableCell>
                      <TableCell>Comment</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Likes</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comments.map(comment => (
                      <TableRow key={comment.id}>
                        <TableCell>
                          <Typography variant="textSM">{comment.createdByUserName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textSM">
                            {comment.content}
                            {comment.isEdited && (
                              <Typography component="span"
                                variant="textXS"
                                color="text.secondary">
                                {' '}(edited)
                              </Typography>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="textXS">{formatDate(comment.createdAtUtc)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ThumbUpIcon fontSize="small"
                              color="action" />
                            <Typography variant="textXS">{comment.likeCount}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Delete comment">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Section>
          </Grid>
        )}


      </Grid>
    </PageArea>
  );
};

export default NewsItemDetails;
